import * as Sentry from '@sentry/react';
import { getCurrentModule } from '@/common/presentation/components/layout/Sidebar/hooks/useMenuRouteObserver';

/**
 * Generates a random string for correlationId
 */
function generateRandomString(): string {
  const webCrypto: Crypto | undefined = typeof globalThis !== 'undefined' ? (globalThis.crypto as Crypto | undefined) : undefined;
  if (webCrypto?.getRandomValues) {
    const array = new Uint8Array(8);
    webCrypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  // Fallback to Math.random if crypto is unavailable
  return Math.random().toString(36).substring(2, 18);
}

/**
 * Generates a correlationId string in the format: module-submodule-randomstring
 */
export const getCorrelationId = (module?: string, submodule?: string): string => {
  const randomString = generateRandomString();
  const parts: string[] = [];

  if (module) {
    parts.push(module);
  }

  if (submodule) {
    parts.push(submodule);
  }

  parts.push(randomString);

  return parts.join('-');
};

function getCorrelationIdKey(): string | undefined {
  try {
    const userName = localStorage.getItem('userName');
    if (!userName) {
      return undefined;
    }
    return `${userName}-correlationId`;
  } catch {
    return undefined;
  }
}

export const generateCorrelationId = (): string | undefined => {
  try {
    const correlationIdKey = getCorrelationIdKey();
    if (!correlationIdKey) {
      return undefined;
    }

    // Get current module and submodule from URL
    const { module, submodule } = getCurrentModule();

    // Generate new correlationId as string: module-submodule-randomstring
    const correlationId = getCorrelationId(module, submodule);

    // Persist to localStorage
    localStorage.setItem(correlationIdKey, correlationId);

    // Update Sentry if available
    try {
      Sentry.setTag('correlationId', correlationId);
    } catch (sentryError) {
      console.error(sentryError);
    }

    return correlationId;
  } catch (error) {
    console.warn('Failed to regenerate correlationId', error);
    return undefined;
  }
};

export const getCorrelationIdFromStorage = (): string | undefined => {
  try {
    const correlationIdKey = getCorrelationIdKey();
    if (correlationIdKey) {
      return localStorage.getItem(correlationIdKey) ?? undefined;
    }
  } catch {
    // Silently fail if localStorage is unavailable
  }
  return undefined;
};

export const clearCorrelationId = (): void => {
  try {
    const correlationIdKey = getCorrelationIdKey();
    if (correlationIdKey) {
      localStorage.removeItem(correlationIdKey);
    }

    // Clear correlationId tag from Sentry
    try {
      Sentry.setTag('correlationId', '');
      Sentry.setContext('user', null);
    } catch (sentryError) {
      console.warn('Failed to clear correlationId in Sentry', sentryError);
    }
  } catch (error) {
    console.warn('Failed to clear correlationId', error);
  }
};
